import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { RoomService } from '@zaoblako/web/core/services/catalog/room.service';
import { GlobalService } from '@zaoblako/web/core/services/global.service';
import { MultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/multi-select/multi-select.component';
import { reconcileSelectionByNumericIds } from '@zaoblako/web/shared/helpers/dropdown-catalog-selection-reconcile.helper';
import { DropdownEntityCacheService } from '@zaoblako/web/shared/services/dropdown-entity-cache.service';
import type { FiltersUiVersion } from '@zaoblako/web/core/types/filters-ui-version.type';
import type { RoomFindInterface } from '@zaoblako/shared/modules/catalog/dtos/room/room-find.dto';

import { isEqual, isNil } from 'lodash';
import { Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  selector: 'crm-room-multi-select',
  standalone: true,
  imports: [CommonModule, MultiSelectComponent],
  templateUrl: './room-multi-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RoomMultiSelectComponent implements OnInit, OnChanges, OnDestroy {
  private static readonly ENTITY_CACHE_KIND = 'room';

  @Input() public filtersUiVersion: FiltersUiVersion = 'v2';

  @Input() public rooms: RoomFindInterface[] = [];

  @Input() public selectedRooms: RoomFindInterface[] = [];
  @Output() public selectedRoomsChange = new EventEmitter<RoomFindInterface[]>();

  @Input() public autoSelectOne = false;
  @Input() public isHaveConverters = false;
  @Input() public onlyParkingAvailable = false;
  @Input() public onlyPacsAvailable = false;
  @Input() public onlyWithHikvisionCameras = false;
  @Input() public onlyWithEquipment = false;
  /** When bound, filters rooms by car category IDs; empty value clears the list */
  @Input() public categoryIds: number[] | null | undefined = undefined;
  /** When bound, filters rooms by filial IDs; empty value clears the list */
  @Input() public filialIds: number[] | null | undefined = undefined;

  @Input() public customRequest = false;
  @Input() public sort: string;
  @Input() public class: string;
  @Input() public styleClass: string;
  @Input() public showClear = true;
  @Input() public disabled = false;
  @Input() public isError = false;
  @Input() public isFloatLabel = true;
  @Input() public placeholder = '\u00A0';
  @Input() public display = 'chip';
  @Input() public appendTo: string;

  private loadedRooms = false;
  private timer: ReturnType<typeof setInterval>;
  private loadSubscription$ = new Subscription();
  private lastAppliedCategoryIds?: number[] | null | undefined;
  private lastAppliedFilialIds?: number[] | null | undefined;
  private inFlightRoomListFingerprint?: string;
  public loading = false;
  /** True while waiting for rooms catalog before applying selected IDs. */
  public selectionLoading = false;

  @Input() public set setSelectedRoomIds(ids: number[]) {
    if (ids?.length) {
      ids.forEach((id, index) => {
        if (typeof id === 'string') {
          ids[index] = +id;
        }
      });
    }

    if (
      ids?.length &&
      ((this.selectedRooms?.length && !this.selectedRooms?.every((selectedRoom) => ids.includes(selectedRoom.id))) ||
        !this.selectedRooms?.length)
    ) {
      if (this.loadedRooms) {
        this.applySelectionFromRequestedIds(ids);
      } else {
        this.selectionLoading = true;
        this.cdRef.markForCheck();
        if (this.timer) {
          clearInterval(this.timer);
        }
        this.timer = setInterval(() => {
          if (this.loadedRooms) {
            this.applySelectionFromRequestedIds(ids);
            clearInterval(this.timer);
          }
        }, 500);
      }
    } else if (!ids?.length) {
      this.selectionLoading = false;
    }
  }

  public constructor(
    private readonly roomService: RoomService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly globalService: GlobalService,
    private readonly entityCache: DropdownEntityCacheService
  ) {}

  public onSelectedRoomsChange(rooms: RoomFindInterface[]): void {
    this.entityCache.putEntities(RoomMultiSelectComponent.ENTITY_CACHE_KIND, rooms ?? []);
    this.selectedRoomsChange.emit(rooms);
  }

  public ngOnInit(): void {
    if (!this.shouldDeferInitialLoadToFilters()) {
      this.loadRooms();
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    let shouldReload = false;

    if (changes['categoryIds']) {
      if (!isEqual(this.lastAppliedCategoryIds, this.categoryIds)) {
        this.lastAppliedCategoryIds = this.categoryIds;
        shouldReload = true;
      }
    }

    if (changes['filialIds']) {
      if (!isEqual(this.lastAppliedFilialIds, this.filialIds)) {
        this.lastAppliedFilialIds = this.filialIds;
        shouldReload = true;
      }
    }

    if (changes['selectedRooms']) {
      this.mergeSelectedRoomsIntoOptions();
      this.cdRef.markForCheck();
    }

    if (shouldReload) {
      this.loadRooms();
    }
  }

  public ngOnDestroy(): void {
    this.loadSubscription$.unsubscribe();
    if (this.timer) {
      clearInterval(this.timer);
    }
  }

  private isCategoryFilterBound(): boolean {
    return this.categoryIds !== undefined;
  }

  private isFilialFilterBound(): boolean {
    return this.filialIds !== undefined;
  }

  private shouldDeferInitialLoadToFilters(): boolean {
    return this.isCategoryFilterBound() || this.isFilialFilterBound();
  }

  private getRoomListFingerprint(): string {
    return this.entityCache.fingerprint({
      isHaveConverters: this.isHaveConverters,
      onlyParkingAvailable: this.onlyParkingAvailable,
      onlyPacsAvailable: this.onlyPacsAvailable,
      onlyWithHikvisionCameras: this.onlyWithHikvisionCameras,
      onlyWithEquipment: this.onlyWithEquipment,
      categoryIds: this.categoryIds,
      filialIds: this.filialIds
    });
  }

  private cancelRoomListFetch(): void {
    this.loadSubscription$.unsubscribe();
    this.loadSubscription$ = new Subscription();
    this.inFlightRoomListFingerprint = undefined;
  }

  private loadRooms(): void {
    const fingerprint = this.getRoomListFingerprint();

    if (this.customRequest) {
      this.cancelRoomListFetch();
      this.loadedRooms = true;
      return;
    }

    if (!this.globalService.globalInfo.tenantInfo?.ratePlan?.modulesAccessControl?.BASIS?.enabled) {
      this.cancelRoomListFetch();
      this.loadedRooms = true;
      return;
    }

    if (this.isCategoryFilterBound() && !this.categoryIds?.length) {
      this.cancelRoomListFetch();
      this.rooms = [];
      this.loadedRooms = true;
      this.loading = false;
      this.filterSelectedRooms();
      this.cdRef.markForCheck();
      return;
    }

    if (this.isFilialFilterBound() && !this.filialIds?.length) {
      this.cancelRoomListFetch();
      this.rooms = [];
      this.loadedRooms = true;
      this.loading = false;
      this.filterSelectedRooms();
      this.cdRef.markForCheck();
      return;
    }

    if (this.inFlightRoomListFingerprint === fingerprint && this.loading) {
      return;
    }

    this.cancelRoomListFetch();
    this.inFlightRoomListFingerprint = fingerprint;
    this.loading = true;
    this.loadedRooms = false;
    this.cdRef.markForCheck();

    this.loadSubscription$.add(
      this.entityCache
        .loadFirstPage(RoomMultiSelectComponent.ENTITY_CACHE_KIND, fingerprint, () =>
          this.roomService
            .findAll({
              isHaveConverters: this.isHaveConverters,
              onlyParkingAvailable: this.onlyParkingAvailable,
              onlyPacsAvailable: this.onlyPacsAvailable,
              onlyWithHikvisionCameras: this.onlyWithHikvisionCameras,
              onlyWithEquipment: this.onlyWithEquipment,
              categoryIds: this.categoryIds?.length ? this.categoryIds : undefined,
              filialIds: this.filialIds?.length ? this.filialIds : undefined
            })
            .pipe(
              map((items) => {
                const list = Array.isArray(items) ? items : [];
                return { items: list, count: list.length };
              })
            )
        )
        .subscribe({
          next: ({ items }) => {
            this.rooms = items;
            this.filterSelectedRooms();

            if (this.autoSelectOne && this.rooms?.length === 1 && !this.selectedRooms?.length) {
              this.selectedRooms = this.rooms;
              this.entityCache.putEntities(RoomMultiSelectComponent.ENTITY_CACHE_KIND, this.selectedRooms);
              this.selectedRoomsChange.emit(this.selectedRooms);
            }

            this.loadedRooms = true;
            this.loading = false;
            this.inFlightRoomListFingerprint = undefined;
            this.cdRef.markForCheck();
          },
          error: () => {
            this.rooms = [];
            this.filterSelectedRooms();
            this.loadedRooms = true;
            this.loading = false;
            this.inFlightRoomListFingerprint = undefined;
            this.cdRef.markForCheck();
            if (this.timer) {
              clearInterval(this.timer);
            }
          }
        })
    );
  }

  private mergeSelectedRoomsIntoOptions(): void {
    const selected = this.selectedRooms ?? [];
    if (!selected.length) {
      return;
    }

    const optionIds = new Set(this.rooms.map(({ id }) => id));
    const missing = selected.filter((room) => !isNil(room?.id) && !optionIds.has(room.id));
    if (!missing.length) {
      return;
    }

    this.rooms = [...this.rooms, ...missing];
  }

  private filterSelectedRooms(): void {
    this.mergeSelectedRoomsIntoOptions();
    const selected = this.selectedRooms ?? [];
    if (selected.length) {
      this.entityCache.putEntities(RoomMultiSelectComponent.ENTITY_CACHE_KIND, selected);
    }
  }

  private applySelectionFromRequestedIds(requestedIds: number[]): void {
    const { selected, hasStaleKeys } = reconcileSelectionByNumericIds(this.rooms, requestedIds);
    this.selectedRooms = selected;
    this.entityCache.putEntities(RoomMultiSelectComponent.ENTITY_CACHE_KIND, selected);
    this.selectionLoading = false;
    if (hasStaleKeys) {
      this.onSelectedRoomsChange(selected);
    }
    this.cdRef.markForCheck();
  }
}
